import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, CalendarDays, X, ExternalLink } from "lucide-react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";

const Bar = ({ pct }) => (
    <div className="h-2 bg-emerald_deep w-full">
        <div
            className="h-full transition-all"
            style={{
                width: `${Math.min(100, pct)}%`,
                background: pct >= 80 ? "#ef4444" : pct >= 50 ? "#f59e0b" : "#d4af37",
            }}
        />
    </div>
);

const cellStyle = (booked, inv) => {
    if (inv <= 0) return "bg-emerald_deep/40 text-muted_green/40";
    if (booked <= 0) return "bg-emerald_deep/50 text-muted_green/60";
    const r = booked / inv;
    if (r < 0.5) return "bg-gold/25 text-gold";
    if (r < 0.8) return "bg-amber-500/40 text-amber-200";
    if (r < 1) return "bg-orange-600/50 text-orange-100";
    return "bg-rose-600/60 text-rose-50";
};

const TR_DAYS = ["Pz", "Pt", "Sa", "Ça", "Pe", "Cu", "Ct"];
const TR_MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

const SummaryView = ({ items }) => (
    <div className="space-y-4">
        {items.map((h) => (
            <div key={h.hotel_id} className="border border-border_green bg-emerald_surface p-6">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1">
                            {h.city === "Mecca" ? "Mekke" : "Medine"}
                        </div>
                        <h3 className="font-serif text-2xl">{h.hotel_name}</h3>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-muted_green">Toplam Doluluk</div>
                        <div className="font-serif text-3xl text-gold">{h.occupancy_percent}%</div>
                        <div className="text-xs text-muted_green">{h.total_booked} / {h.total_inventory} oda</div>
                    </div>
                </div>
                <Bar pct={h.occupancy_percent} />
                <div className="mt-6 space-y-3">
                    {h.rooms.map((r) => (
                        <div key={r.room_id} className="grid grid-cols-12 gap-4 items-center text-sm">
                            <div className="col-span-3">{r.room_name}</div>
                            <div className="col-span-6"><Bar pct={r.occupancy_percent} /></div>
                            <div className="col-span-2 text-right text-muted_green text-xs">{r.booked} / {r.inventory}</div>
                            <div className="col-span-1 text-right text-gold">{r.occupancy_percent}%</div>
                        </div>
                    ))}
                </div>
            </div>
        ))}
    </div>
);

const CalendarView = ({ cal, onCellClick }) => {
    if (!cal) return <div className="text-muted_green text-sm py-8">Yükleniyor…</div>;
    const dates = (cal.dates || []).map((iso) => {
        const d = new Date(iso + "T00:00:00");
        return { iso, day: d.getDate(), wd: TR_DAYS[d.getDay()], month: TR_MONTHS[d.getMonth()], isWeekend: d.getDay() === 0 || d.getDay() === 6, isFirst: d.getDate() === 1 };
    });

    return (
        <div className="space-y-8">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted_green" data-testid="occupancy-legend">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 inline-block bg-emerald_deep/50 border border-border_green" /> Boş</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 inline-block bg-gold/25" /> %50'ye kadar</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 inline-block bg-amber-500/40" /> %50-80</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 inline-block bg-orange-600/50" /> %80+</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 inline-block bg-rose-600/60" /> Dolu</span>
                <span className="ml-auto">Hücre değeri = dolu oda sayısı</span>
            </div>
            {(cal.hotels || []).map((h) => (
                <div key={h.hotel_id} className="border border-border_green bg-emerald_surface" data-testid={`occ-cal-hotel-${h.hotel_id}`}>
                    <div className="px-4 sm:px-5 py-3 border-b border-border_green flex items-center gap-3">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/40 px-2 py-0.5">
                            {h.city === "Mecca" ? "Mekke" : "Medine"}
                        </span>
                        <h3 className="font-serif text-xl">{h.hotel_name}</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="border-collapse w-full">
                            <thead>
                                <tr>
                                    <th className="sticky left-0 z-10 bg-emerald_surface text-left text-[10px] uppercase tracking-[0.15em] text-muted_green font-semibold px-3 py-2 min-w-[130px] border-b border-border_green">Oda Tipi</th>
                                    {dates.map((d) => (
                                        <th key={d.iso} className={`text-center px-0 py-1.5 border-b border-border_green min-w-[34px] ${d.isWeekend ? "bg-emerald_deep/40" : ""}`}>
                                            {(d.isFirst || d.iso === dates[0].iso) && (
                                                <div className="text-[9px] text-gold font-semibold">{d.month}</div>
                                            )}
                                            <div className="text-[10px] text-bone/80 font-semibold">{d.day}</div>
                                            <div className="text-[8px] text-muted_green">{d.wd}</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {h.rooms.map((r) => (
                                    <tr key={r.room_id} data-testid={`occ-cal-room-${r.room_id}`}>
                                        <td className="sticky left-0 z-10 bg-emerald_surface px-3 py-1.5 text-xs border-b border-border_green/50 whitespace-nowrap">
                                            <div className="text-bone/90">{r.room_name}</div>
                                            <div className="text-[9px] text-muted_green">{r.inventory} oda envanteri</div>
                                        </td>
                                        {r.daily.map((booked, i) => {
                                            const d = dates[i];
                                            const dayRes = (r.reservations || []).filter((rv) => rv.check_in <= d.iso && d.iso < rv.check_out);
                                            const resInfo = dayRes
                                                .map((rv) => `${rv.code || ""} ${rv.customer_name || ""} (${rv.rooms_count} oda, ${rv.status === "approved" ? "onaylı" : "beklemede"})`)
                                                .join("\n");
                                            return (
                                                <td
                                                    key={d.iso}
                                                    title={`${d.day} ${d.month}: ${booked}/${r.inventory} dolu${resInfo ? "\n" + resInfo : ""}`}
                                                    onClick={() => onCellClick({ hotel: h, room: r, date: d, booked, reservations: dayRes })}
                                                    className={`text-center text-[10px] font-mono font-semibold border-b border-border_green/30 border-l border-l-emerald_deep/60 h-8 cursor-pointer hover:ring-1 hover:ring-inset hover:ring-gold ${cellStyle(booked, r.inventory)}`}
                                                    data-testid={`occ-cell-${r.room_id}-${d.iso}`}
                                                >
                                                    {booked > 0 ? booked : ""}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </div>
    );
};

const DayPanel = ({ panel, onClose }) => {
    const navigate = useNavigate();
    if (!panel) return null;
    const { hotel, room, date, booked, reservations } = panel;
    return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="occ-day-panel">
            <div className="absolute inset-0 bg-emerald_deep/70 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-md h-full bg-emerald_surface border-l border-gold/40 overflow-y-auto">
                <div className="p-5 border-b border-border_green flex items-start justify-between gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1">
                            {date.day} {date.month} · {hotel.city === "Mecca" ? "Mekke" : "Medine"}
                        </div>
                        <h3 className="font-serif text-2xl leading-tight">{hotel.hotel_name}</h3>
                        <div className="text-sm text-muted_green mt-1">{room.room_name} — {booked}/{room.inventory} oda dolu</div>
                    </div>
                    <button onClick={onClose} className="p-2 border border-border_green text-muted_green hover:text-gold hover:border-gold" data-testid="occ-panel-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5 space-y-3">
                    {reservations.length === 0 ? (
                        <div className="border border-dashed border-border_green p-8 text-center text-sm text-muted_green" data-testid="occ-panel-empty">
                            Bu gün için rezervasyon yok.
                        </div>
                    ) : (
                        reservations.map((rv, i) => (
                            <div key={rv.code || i} className="border border-border_green bg-emerald_deep/40 p-4" data-testid={`occ-panel-res-${i}`}>
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <button
                                        onClick={() => navigate(`/admin/reservations?q=${encodeURIComponent(rv.code || "")}`)}
                                        className="font-mono text-gold text-sm inline-flex items-center gap-1.5 hover:underline"
                                        data-testid={`occ-panel-code-${i}`}
                                    >
                                        {rv.code || "—"} <ExternalLink className="w-3 h-3" />
                                    </button>
                                    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border font-semibold ${
                                        rv.status === "approved" ? "text-emerald-400 border-emerald-400/40" : "text-yellow-400 border-yellow-400/40"
                                    }`}>{rv.status === "approved" ? "Onaylı" : "Beklemede"}</span>
                                </div>
                                <div className="text-sm">{rv.customer_name}</div>
                                <div className="text-xs text-muted_green mt-1">
                                    {rv.check_in} → {rv.check_out} · {rv.rooms_count} oda
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

const AdminOccupancy = () => {
    const { t } = useApp();
    const [view, setView] = useState("summary");
    const [items, setItems] = useState([]);
    const [cal, setCal] = useState(null);
    const [days, setDays] = useState(30);
    const [panel, setPanel] = useState(null);

    useEffect(() => {
        api.get("/admin/occupancy").then(({ data }) => setItems(data));
    }, []);

    useEffect(() => {
        if (view !== "calendar") return;
        setCal(null);
        api.get(`/admin/occupancy/calendar?days=${days}`).then(({ data }) => setCal(data)).catch(() => setCal({ hotels: [], dates: [] }));
    }, [view, days]);

    return (
        <div>
            <h1 className="font-serif text-4xl mb-2">{t("admin.occupancy")}</h1>
            <p className="text-muted_green mb-6">Doluluk oranları (onaylı + bekleyen rezervasyonlar)</p>

            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setView("summary")}
                        className={`text-xs uppercase tracking-[0.15em] px-4 py-2 border font-semibold flex items-center gap-2 transition-colors ${view === "summary" ? "bg-gold text-emerald_deep border-gold" : "border-border_green text-muted_green hover:border-gold hover:text-gold"}`}
                        data-testid="occ-view-summary"
                    >
                        <BarChart3 className="w-3.5 h-3.5" /> Özet
                    </button>
                    <button
                        onClick={() => setView("calendar")}
                        className={`text-xs uppercase tracking-[0.15em] px-4 py-2 border font-semibold flex items-center gap-2 transition-colors ${view === "calendar" ? "bg-gold text-emerald_deep border-gold" : "border-border_green text-muted_green hover:border-gold hover:text-gold"}`}
                        data-testid="occ-view-calendar"
                    >
                        <CalendarDays className="w-3.5 h-3.5" /> Takvim
                    </button>
                </div>
                {view === "calendar" && (
                    <select
                        className="input-dark !w-auto !py-2 text-xs"
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        data-testid="occ-days-select"
                    >
                        <option value={30} className="bg-emerald_surface">30 gün</option>
                        <option value={60} className="bg-emerald_surface">60 gün</option>
                        <option value={90} className="bg-emerald_surface">90 gün</option>
                    </select>
                )}
            </div>

            {view === "summary" ? <SummaryView items={items} /> : <CalendarView cal={cal} onCellClick={setPanel} />}
            <DayPanel panel={panel} onClose={() => setPanel(null)} />
        </div>
    );
};

export default AdminOccupancy;
